import React, { createContext, useContext, useState, useEffect } from "react";
import { useMatches } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

interface PageTitleContextType {
    titlePrefix: string | null;
    setTitlePrefix: (prefix: string | null) => void;
}

const PageTitleContext = createContext<PageTitleContextType | undefined>(undefined);

export function PageTitleProvider({ children }: { children: React.ReactNode }) {
    const [titlePrefix, setTitlePrefix] = useState<string | null>(null);

    return (
        <PageTitleContext.Provider value={{ titlePrefix, setTitlePrefix }}>
            <TitleManager />
            {children}
        </PageTitleContext.Provider>
    );
}

function TitleManager() {
    const { titlePrefix } = usePageTitle();
    const matches = useMatches();
    const { t } = useTranslation();

    useEffect(() => {
        const matchWithTitle = [...matches].reverse().find((match) => match.staticData?.title);
        const staticTitleKey = matchWithTitle?.staticData?.title;

        if (staticTitleKey) {
            const translatedTitle = t(staticTitleKey);
            if (titlePrefix) {
                document.title = `${titlePrefix} - ${translatedTitle}`;
            } else {
                document.title = translatedTitle;
            }
        } else if (titlePrefix) {
            document.title = titlePrefix;
        }
    }, [matches, titlePrefix, t]);

    return null;
}

export function usePageTitle() {
    const context = useContext(PageTitleContext);
    if (!context) {
        throw new Error("usePageTitle must be used within a PageTitleProvider");
    }
    return context;
}
